import { useState, useEffect, useCallback } from "react";
import {
  Target, CheckCircle, XCircle, Clock, ExternalLink,
  Music, Star, Filter, ChevronDown, ChevronUp
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const IncomingSubmissionsTab = ({ authToken }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const headers = {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submission-targets/incoming`, { headers });
      const data = await res.json();
      setSubmissions(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const updateStatus = async (targetId, status) => {
    try {
      await fetch(`${BACKEND_URL}/api/submission-targets/${targetId}/status`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status }),
      });
      fetchSubmissions();
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = submissions.filter((s) => {
    if (filter === "all") return true;
    return s.target_status === filter;
  });

  const counts = {
    all: submissions.length,
    pending: submissions.filter((s) => s.target_status === "pending").length,
    approved: submissions.filter((s) => s.target_status === "approved").length,
    rejected: submissions.filter((s) => s.target_status === "rejected").length,
  };

  const statusColors = {
    pending: "text-amber-400 bg-amber-400/10",
    approved: "text-green-400 bg-green-400/10",
    rejected: "text-red-400 bg-red-400/10",
  };

  return (
    <div className="space-y-6" data-testid="incoming-submissions-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Incoming Submissions</h2>
            <p className="text-white/40 text-xs">Tracks submitted to your station</p>
          </div>
        </div>
        <button
          onClick={fetchSubmissions}
          disabled={loading}
          className="text-white/50 hover:text-white text-xs bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition"
          data-testid="incoming-refresh-btn"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { key: "all", label: "Total", color: "text-white" },
          { key: "pending", label: "Pending", color: "text-amber-400" },
          { key: "approved", label: "Approved", color: "text-green-400" },
          { key: "rejected", label: "Rejected", color: "text-red-400" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`p-3 rounded-xl text-center transition border ${
              filter === f.key
                ? "border-purple-500/50 bg-purple-500/10"
                : "border-white/5 bg-white/[0.02] hover:bg-white/5"
            }`}
            data-testid={`incoming-filter-${f.key}`}
          >
            <p className={`text-xl font-bold ${f.color}`}>{counts[f.key]}</p>
            <p className="text-white/40 text-xs">{f.label}</p>
          </button>
        ))}
      </div>

      {/* Submissions List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] rounded-2xl border border-white/5">
          <Music className="w-10 h-10 mx-auto mb-3 text-white/10" />
          <p className="text-white/30 text-sm">
            {filter === "all"
              ? "No submissions targeted to your station yet"
              : `No ${filter} submissions`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((sub) => (
            <div
              key={sub.target_id}
              className={`bg-white/[0.02] border rounded-xl transition ${
                sub.is_priority
                  ? "border-amber-400/20"
                  : "border-white/5"
              }`}
              data-testid={`incoming-submission-${sub.target_id}`}
            >
              {/* Main Row */}
              <div
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/[0.02] transition"
                onClick={() => setExpanded(expanded === sub.target_id ? null : sub.target_id)}
              >
                {sub.is_priority && (
                  <Star className="w-4 h-4 text-amber-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-semibold text-sm truncate">
                      {sub.track_title}
                    </p>
                    {sub.is_priority && (
                      <span className="text-[10px] font-bold text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0">
                        Priority
                      </span>
                    )}
                    {sub.featured && (
                      <span className="text-[10px] font-bold text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 rounded-full uppercase tracking-wide flex-shrink-0">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-white/50 text-xs truncate">
                    {sub.artist_name} {sub.genre ? `· ${sub.genre}` : ""} {sub.bpm ? `· ${sub.bpm} BPM` : ""}
                  </p>
                </div>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[sub.target_status] || "text-white/40 bg-white/5"}`}>
                  {sub.target_status}
                </span>
                {expanded === sub.target_id ? (
                  <ChevronUp className="w-4 h-4 text-white/30" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white/30" />
                )}
              </div>

              {/* Expanded Details */}
              {expanded === sub.target_id && (
                <div className="px-4 pb-4 pt-1 border-t border-white/5 space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-white/30">Email:</span>{" "}
                      <span className="text-white/70">{sub.email}</span>
                    </div>
                    <div>
                      <span className="text-white/30">PRO:</span>{" "}
                      <span className="text-white/70">{sub.pro_affiliation}</span>
                    </div>
                    {sub.is_explicit && (
                      <div>
                        <span className="text-red-400 font-medium">Explicit Content</span>
                      </div>
                    )}
                    <div>
                      <span className="text-white/30">Submitted:</span>{" "}
                      <span className="text-white/70">
                        {new Date(sub.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {sub.streaming_link && (
                    <a
                      href={sub.streaming_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-xs transition"
                      data-testid={`incoming-link-${sub.target_id}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Listen / Download Track
                    </a>
                  )}

                  {sub.message && (
                    <p className="text-white/40 text-xs italic">"{sub.message}"</p>
                  )}

                  {/* Action Buttons */}
                  {sub.target_status === "pending" && (
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => updateStatus(sub.target_id, "approved")}
                        className="flex items-center gap-1.5 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs font-medium px-4 py-2 rounded-lg transition"
                        data-testid={`incoming-approve-${sub.target_id}`}
                      >
                        <CheckCircle className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => updateStatus(sub.target_id, "rejected")}
                        className="flex items-center gap-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs font-medium px-4 py-2 rounded-lg transition"
                        data-testid={`incoming-reject-${sub.target_id}`}
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  )}

                  {sub.target_status !== "pending" && (
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => updateStatus(sub.target_id, "pending")}
                        className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white/50 text-xs font-medium px-4 py-2 rounded-lg transition"
                        data-testid={`incoming-reset-${sub.target_id}`}
                      >
                        <Clock className="w-3.5 h-3.5" /> Reset to Pending
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IncomingSubmissionsTab;
